import { findLiveConnection, type ConnectionView } from './connections';
import type { Mapping } from './mappings';

/**
 * The setup checklist, derived rather than declared.
 *
 * The Overview used to hard-code five rows with step 1 done and the rest
 * "Waiting" — true in Phase 0, and still saying "arrives in the next release"
 * long after every phase shipped. Everything below is read from
 * `GET /calendar/connections` and `GET /calendar/mappings`, so the screen can
 * only claim what the adapter can actually show.
 *
 * The wording is for whoever is doing the setting up, not for an operator:
 * each row says what is true and, when it is not done, the one thing that
 * would move it on.
 */

export type StepState = 'done' | 'in_progress' | 'attention' | 'waiting' | 'checking';

export interface SetupStep {
  number: number;
  title: string;
  detail: string;
  state: StepState;
  /**
   * Where to go to move this step on.
   *
   * A checklist that names a screen in prose and then makes you find it is a
   * checklist people ask about rather than act on. Only set where there is one
   * obvious place to go — a step that is done, or waiting on something
   * automatic, has nowhere useful to send anyone.
   */
  action?: { label: string; to: string };
}

export interface SetupSummary {
  /** The connection the rest of the app is using, if there is one. */
  connection: ConnectionView | null;
  /** Null while unknown — a zero here would read as "we looked and found none". */
  roomsDiscovered: number | null;
  spacesMapped: number | null;
  steps: SetupStep[];
}

export interface SetupInput {
  /** Organisation name and domain, for step 1. Always known: we are signed in. */
  organizationLabel: string;
  /** Null until `GET /calendar/connections` answers. */
  connections: ConnectionView[] | null;
  /** Null until `GET /calendar/mappings` answers. */
  mappings: Mapping[] | null;
}

export function summariseSetup({
  organizationLabel,
  connections,
  mappings,
}: SetupInput): SetupSummary {
  const connection = connections ? findLiveConnection(connections) : null;

  // Summed rather than taken from the live connection: nothing stops an
  // organisation connecting both Google and Microsoft, and the figure on the
  // Overview is "rooms we know about", not "rooms on one connection".
  const roomsDiscovered = connections
    ? connections.reduce((total, c) => total + (c.connection_id && c.can_read ? c.rooms_discovered : 0), 0)
    : null;

  return {
    connection,
    roomsDiscovered,
    spacesMapped: mappings ? mappings.length : null,
    steps: [
      { number: 1, title: 'Connect ZenCore', ...zenCoreStep(organizationLabel) },
      { number: 2, title: 'Connect a calendar', ...calendarStep(connections) },
      {
        number: 3,
        title: 'Map spaces to resources',
        ...mappingStep(connections, connection, mappings, roomsDiscovered),
      },
      { number: 4, title: 'Sync the directory', ...directoryStep(connections, connection) },
      { number: 5, title: 'Hand off to ZenCore', ...handoffStep(connections, mappings) },
    ],
  };
}

type StepBody = Pick<SetupStep, 'detail' | 'state' | 'action'>;

/** The screens a step can send someone to. */
const GO = {
  connections: { label: 'Go to Connections', to: '/connections' },
  rooms: { label: 'Go to Discovered rooms', to: '/rooms' },
  mappings: { label: 'Go to Space mapping', to: '/mappings' },
} as const;

/** Step 1 is done by definition: this screen is behind a ZenCore sign-in. */
function zenCoreStep(organizationLabel: string): StepBody {
  return {
    state: 'done',
    detail: `${organizationLabel} — your sign-in is scoped to this organization.`,
  };
}

function calendarStep(connections: ConnectionView[] | null): StepBody {
  if (!connections) return { state: 'checking', detail: 'Checking your connections…' };

  // A step this organisation has not taken yet, not a deployment problem: every
  // customer registers its own OAuth application, so the person reading this is
  // the person who can move it on.
  if (connections.length > 0 && connections.every((c) => !c.app_configured)) {
    return {
      state: 'waiting',
      detail: 'Register your own Google or Microsoft application first.',
      action: GO.connections,
    };
  }

  const broken = connections.find((c) => c.state === 'broken');
  if (broken) {
    return {
      state: 'attention',
      detail: broken.next_step ?? `${broken.display_name} needs reconnecting.`,
      action: GO.connections,
    };
  }

  const live = findLiveConnection(connections);
  if (live) {
    const where = live.domain ? `${live.display_name} · ${live.domain}` : live.display_name;
    // `can_write` comes only from a completed write probe, so read-without-
    // write is a real state and the checklist says so instead of ticking.
    return live.can_write
      ? { state: 'done', detail: `${where} — we can read your rooms and book them.` }
      : {
          state: 'attention',
          detail: `${where} — we can see your rooms but cannot book them yet.`,
          action: GO.connections,
        };
  }

  const inFlight = connections.find((c) => c.state === 'connecting' || c.state === 'verifying');
  if (inFlight) {
    return {
      state: 'in_progress',
      detail:
        inFlight.state === 'verifying'
          ? `Checking what we can do with ${inFlight.display_name}.`
          : `Waiting for admin consent on ${inFlight.display_name}.`,
    };
  }

  const registered = connections.find((c) => c.app_configured);
  return {
    state: 'waiting',
    detail: registered
      ? `Your ${registered.display_name} application is registered. Connect it with admin consent.`
      : 'Microsoft 365 or Google Workspace, with admin consent.',
    action: GO.connections,
  };
}

function mappingStep(
  connections: ConnectionView[] | null,
  connection: ConnectionView | null,
  mappings: Mapping[] | null,
  roomsDiscovered: number | null,
): StepBody {
  if (!connections || !mappings) return { state: 'checking', detail: 'Checking your mappings…' };

  if (mappings.length > 0) {
    return {
      state: 'done',
      detail: `${count(mappings.length, 'space')} linked to a room, one to one.`,
    };
  }

  if (!connection) return { state: 'waiting', detail: 'Waiting for a calendar connection.' };
  if (!roomsDiscovered) return { state: 'waiting', detail: 'Waiting for rooms to be discovered.' };

  return {
    state: 'waiting',
    detail: `${count(roomsDiscovered, 'room')} to link.`,
    action: GO.mappings,
  };
}

function directoryStep(
  connections: ConnectionView[] | null,
  connection: ConnectionView | null,
): StepBody {
  if (!connections) return { state: 'checking', detail: 'Checking the room directory…' };
  if (!connection) return { state: 'waiting', detail: 'Comes with the calendar connection.' };

  if (!connection.last_discovery_at) {
    return { state: 'in_progress', detail: `Reading the room directory from ${connection.display_name}.` };
  }

  const when = new Date(connection.last_discovery_at).toLocaleString();

  // Read the directory, found nothing: the connection works, so this is a
  // question about the customer's directory rather than about the connection.
  if (connection.rooms_discovered === 0) {
    return {
      state: 'attention',
      detail: `We read ${connection.display_name} on ${when} and found no bookable rooms.`,
      action: GO.rooms,
    };
  }

  return {
    state: 'done',
    detail: `${count(connection.rooms_discovered, 'room')} read from ${connection.display_name}, last on ${when}.`,
  };
}

/**
 * Step 5 is not an action anyone takes — it is whether the two subscriptions
 * behind each mapping actually registered. `active` is the only state that
 * means bookings flow both ways, so it is the only one that ticks.
 */
function handoffStep(
  connections: ConnectionView[] | null,
  mappings: Mapping[] | null,
): StepBody {
  if (!connections || !mappings) return { state: 'checking', detail: 'Checking registrations…' };

  // Said BEFORE anyone maps, not after. Without the key every mapping stops
  // half-registered — the calendar side live, ZenCore's side never — and five
  // hundred pods in that state all have to be re-applied.
  const connected = connections.some((c) => c.connection_id && c.can_read);
  if (connected && !connections.some((c) => c.zencore_key_configured)) {
    return {
      state: 'attention',
      detail:
        'Add your ZenSpace API key. Without it, bookings made in ZenSpace will not reach ' +
        'the room.',
      action: GO.connections,
    };
  }

  if (mappings.length === 0) {
    return { state: 'waiting', detail: 'Displays and sensors go live once a space is mapped.' };
  }

  const failed = mappings.filter((m) => m.registration_state === 'failed').length;
  if (failed > 0) {
    return {
      state: 'attention',
      detail: `${failed} of ${mappings.length} could not finish registering. Re-applying picks up only what is missing.`,
      action: GO.mappings,
    };
  }

  const active = mappings.filter((m) => m.registration_state === 'active').length;
  if (active === mappings.length) {
    return {
      state: 'done',
      detail: `${count(active, 'space')} live — ZenCore is driving displays and sensors.`,
    };
  }

  return {
    state: 'in_progress',
    detail: `${active} of ${mappings.length} registered both ways. The rest are still going through.`,
  };
}

/** "1 room" / "2 rooms" — a bare number beside a noun reads as a defect. */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}
